import { useEffect, useState } from 'react';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Col, Row } from 'react-bootstrap';
import { Dropdown } from 'primereact/dropdown';
import VehicleEditDialog from './VehicleEditDialog/VehicleEditDialog';

function VehiclesDataTable() {

  // #region vars and consts

  const [vehicles, setVehicles] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);  
  const [loading, setLoading] = useState(false);
  const [editDialogIsVisible, setEditDialogIsVisible] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const activeTypes = [{ label: 'Activo', value: true }, { label: 'Inactivo', value: false }]
  const [filters, setFilters] = useState({
    'global': {
      value: null,
      matchMode: FilterMatchMode.CONTAINS
    },
    "callSign": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.CONTAINS
      }]
    },
    "licensePlate": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.CONTAINS
      }]
    },
    "description": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.CONTAINS
      }]
    },
    "capacityBags": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.GREATER_THAN
      }]
    },
    "seats": {
      operator: FilterOperator.AND,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.GREATER_THAN
      }]
    },
    "vehicleType.vehicleTypeId": {
      operator: FilterOperator.OR,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.EQUALS
      }]
    },
    "active": {
      operator: FilterOperator.OR,
      constraints: [{
        value: null,
        matchMode:
          FilterMatchMode.EQUALS
      }]
    }    
  });

  // #endregion

  // #region methods

  const getVehicles = () => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/vehicle/findAll`)
      .then(response => response.json())
      .then(data => {
        if (data.responseCode === 'OK') {
          setVehicles(data.responseObject);
        } else {
          console.warn('VehicleDataTable: getVehicles: error: data:', data);
        }
        setLoading(false);
      });
  };

  const getVehicleTypes = () => {    
    fetch(`${process.env.REACT_APP_API_URL}/vehicleType/cmb?generic=true`)
      .then(response => response.json())
      .then(data => {
        if (data.responseCode === 'OK') {
          setVehicleTypes(data.responseObject);          
        } else {
          console.warn('VehicleDataTable: getVehicleTypes: error: data:', data);
        }
      });
  };

  // #endregion 

  // #region events

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    const _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilter(value);
  }

  const onNewClick = () => {
    setSelectedVehicle(null);
    setEditDialogIsVisible(true);
  };

  const onVehicleSaved = () => {
    setEditDialogIsVisible(false);
    getVehicles();
  };

  const onEditClick = (vehicle) => {
    setSelectedVehicle(vehicle);
    setEditDialogIsVisible(true);
  };

  // #endregion

  // #region templates

  const headerTemplate = (
    <>
      <Row>
        <Col md={8}>
          <span className="p-input-icon-left">
            <i className="pi pi-filter" />
            <InputText
              placeholder="Filtrar"
              value={globalFilter}
              onChange={onGlobalFilterChange} />
          </span>
        </Col>
        <Col md={4}>
          <Button
            label="Nuevo"
            className="p-button-raised p-button-sm float-end"
            onClick={onNewClick}
          />
        </Col>
      </Row>
    </>
  );

  const dataTableActionsTemplate = (vehicle) => {
    return (
      <>
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-primary p-button-text mr-2"
          onClick={() => onEditClick(vehicle)} />
      </>
    );
  };

  const vehicleTypeIdFieldTemplate = (vehicle) => {
    return vehicle.vehicleType.description;
  };

  const vehicleTypeFilterTemplate = (option) => {
    return <span>{option.label}</span>;
  };

  const vehicleTypesFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={vehicleTypes}
        onChange={(e) => options.filterCallback(e.value)}
        itemTemplate={vehicleTypeFilterTemplate}
        placeholder="Selecciona un tipo de vehículo"        
        showClear />
    );
  };

  const activeFieldTemplate = (vehicle) => {
    return (
      vehicle.active? <i className="pi pi-check text-primary"></i>: <></>
    );
  }

  const activeTypeFilterTemplate = (option) => {
    return <span>{option.label}</span>;
  };

  const activeTypesFilterTemplate = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={activeTypes}
        onChange={(e) => options.filterCallback(e.value)}
        itemTemplate={activeTypeFilterTemplate}
        placeholder="Selecciona el estatus activo"
        showClear />
    );
  };

  const dataTableTemplate = (
    <>
      <div className="card">
        <DataTable
          className=""
          showGridlines
          stripedRows
          size="small"
          responsiveLayout="scroll"
          emptyMessage="No se encontraron vehícles"
          value={vehicles}
          header={headerTemplate}
          filters={filters}
          filterDisplay="menu"
          loading={loading}
          resizableColumns
          columnResizeMode="fit"
          globalFilterFields={[            
            "callSign",
            "licensePlate",
            "description",
            "capacityBags",
            "seats",
            "vehicleType.description",
            "active"
          ]}>
          <Column
            className='app-w-50-px text-center'
            body={dataTableActionsTemplate}
            exportable={false} />
          <Column
            field="callSign"
            header="No. exterior"
            className='app-w-200-px text-start'
            filter
            filterPlaceholder="Buscar por No. exterior"
            sortable />
          <Column
            field="licensePlate"
            header="Placa"
            className='app-w-200-px text-start'
            filter
            filterPlaceholder="Buscar por Placa"
            sortable />
          <Column
            field="description"
            header="Descripción"
            className='app-w-200-px text-start'
            filter
            filterPlaceholder="Buscar por Descripción"
            sortable />
          <Column
            field="capacityBags"
            header="Capacidad de maletas"
            className='app-w-200-px text-end'
            dataType="numeric"
            filter
            filterPlaceholder="Buscar por Capacidad de maletas"
            sortable />
          <Column
            field="seats"
            header="Asientos"
            className='app-w-200-px text-end'
            dataType="numeric"
            filter
            filterPlaceholder="Buscar por Asientos"
            sortable />
          <Column
            field="vehicleType.vehicleTypeId"
            header="Tipo de vehículo"
            className='app-w-200-px text-start'
            filter
            filterPlaceholder="Buscar por Tipo de vehiculo"
            filterElement={vehicleTypesFilterTemplate}
            body={vehicleTypeIdFieldTemplate}
            sortable />
          <Column
            field="active"
            header="Activo"
            className='app-w-200-px text-center'            
            filter
            filterPlaceholder="Buscar por Activo"
            filterElement={activeTypesFilterTemplate}
            body={activeFieldTemplate}
            sortable />
        </DataTable>
      </div>
    </>
  );

  // #endregion

  useEffect(() => {
    getVehicles();
    getVehicleTypes();
  }, []);

  return (
    <>
      <article>
        {dataTableTemplate}
        <VehicleEditDialog
          visible={editDialogIsVisible}
          onHide={() => setEditDialogIsVisible(false)}
          onVehicleSaved={onVehicleSaved}
          vehicle={selectedVehicle}
        />
      </article>
    </>
  );
}

export default VehiclesDataTable;